import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import Modal from 'component/Modal'
import TextField from 'component/TextField'
import TextArea from 'component/TextArea'
import './styles.css'

const ExternalApiEditModal = ({
  isOpen,
  record,
  translate,
  onClose,
  onSave,
  onEdit,
  isUpdating,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState({
    description: '',
    urlDev: '',
    urlPrd: '',
    apiKeyDev: '',
    apiKeyPrd: '',
  })
  const [copiedField, setCopiedField] = useState(null)

  useEffect(() => {
    if (!record) return

    setFormData({
      description: record.description || '',
      urlDev: record.urlDev || '',
      urlPrd: record.urlPrd || '',
      apiKeyDev: record.apiKeyDev || '',
      apiKeyPrd: record.apiKeyPrd || '',
    })
  }, [record])

  if (!isOpen || !record) return null

  const isVaultRecord = record.usesVault

  const hasRequiredFieldsFilled = () => {
    const requiredFields = ['description', 'urlDev', 'urlPrd']
    return requiredFields.every((field) => String(formData[field] || '').trim())
  }

  const hasChanges = () => {
    if (formData.description !== (record.description || '')) return true
    if (formData.urlDev !== (record.urlDev || '')) return true
    if (formData.urlPrd !== (record.urlPrd || '')) return true
    if (!isVaultRecord) {
      if (formData.apiKeyDev !== (record.apiKeyDev || '')) return true
      if (formData.apiKeyPrd !== (record.apiKeyPrd || '')) return true
    }
    return false
  }

  const handleChange = (field) => (event) => {
    const value = event && event.target ? event.target.value : event
    setFormData((currentFormData) => ({
      ...currentFormData,
      [field]: value,
    }))
  }

  const handleSave = () => {
    if (readOnly) {
      return
    }

    const updatedData = {
      ...record,
      description: formData.description,
      urlDev: formData.urlDev,
      urlPrd: formData.urlPrd,
    }

    if (!isVaultRecord) {
      updatedData.apiKeyDev = formData.apiKeyDev
      updatedData.apiKeyPrd = formData.apiKeyPrd
    }

    onSave(updatedData)
  }

  const handleCopy = (field, valueToCopy) => {
    if (!valueToCopy) {
      return
    }

    navigator.clipboard.writeText(valueToCopy).then(() => {
      setCopiedField(field)
      window.setTimeout(() => {
        setCopiedField((currentField) => (currentField === field ? null : currentField))
      }, 2000)
    })
  }

  const renderCopyableTextField = ({
    field,
    label,
    value,
    onChange,
    ariaLabel,
    disabled = readOnly,
  }) => (
    <div className='external-apis-management-copy-field'>
      <div className='external-apis-management-copy-label'>
        <span>{label}</span>
        <button
          type='button'
          className={`external-apis-management-copy-value ${
            copiedField === field ? 'external-apis-management-copy-value-active' : ''
          }`}
          onClick={() => handleCopy(field, value)}
          aria-label={ariaLabel}
          title={ariaLabel}
        >
          {copiedField === field ? 'Copiado!' : 'Copiar valor'}
        </button>
      </div>
      <TextField
        controlled
        value={value}
        fill
        disabled={disabled}
        label=''
        name={field}
        onChange={onChange}
      />
    </div>
  )

  const renderCopyableTextArea = ({
    field,
    label,
    value,
    onChange,
    ariaLabel,
    disabled = readOnly,
  }) => (
    <div className='external-apis-management-copy-field external-apis-management-url-field'>
      <div className='external-apis-management-copy-label'>
        <span>{label}</span>
        <button
          type='button'
          className={`external-apis-management-copy-value ${
            copiedField === field ? 'external-apis-management-copy-value-active' : ''
          }`}
          onClick={() => handleCopy(field, value)}
          aria-label={ariaLabel}
          title={ariaLabel}
        >
          {copiedField === field ? 'Copiado!' : 'Copiar valor'}
        </button>
      </div>
      <TextArea
        controlled
        disabled={disabled}
        label=''
        name={field}
        className='form-control-textarea external-apis-management-url-textarea'
        value={value}
        onChange={onChange}
      />
    </div>
  )

  const renderViewCopyLabel = (field, label, value) => (
    <div className='w-100 margin-top color-default'>
      {label}
      {value && (
        <span
          className='copyValue margin-left-15'
          onClick={() => handleCopy(field, value)}
          style={{ cursor: 'pointer', color: copiedField === field ? '#158110' : '#41A017' }}
        >
          {copiedField === field ? 'Copiado!' : 'Copiar valor'}
        </span>
      )}
    </div>
  )

  const handleEditFromView = () => {
    if (onEdit) {
      onEdit()
    }
  }

  if (readOnly) {
    return (
      <Modal
        title={translate('externalApisManagement.view.title')}
        confirmButtonText={translate('edit_button_label')}
        cancelButtonText={translate('cancel_button')}
        isOpen
        size='md'
        className='external-apis-management-modal external-apis-management-modal-view'
        onCloseModal={onClose}
        onConfirm={handleEditFromView}
        noCloseOnConfirm
      >
        <div className='external-apis-management-edit-modal'>
          <div className='container-parameter'>
            <div className='parameter-item'>
              <div className='w-100 margin-top color-default'>
                {translate('externalApisManagement.table.name')}
              </div>
              <div className='w-100 sm-margin-top'>{record.name}</div>
            </div>
            <div>
              <div className='w-100 margin-top color-default'>
                {translate('externalApisManagement.table.syncVault')}
              </div>
              <div className='w-100 sm-margin-top'>
                {record.usesVault
                  ? translate('externalApisManagement.target.vault')
                  : translate('externalApisManagement.target.custom')}
              </div>
            </div>
          </div>

          <div className='w-100 margin-top color-default'>
            {translate('externalApisManagement.table.description')}
          </div>
          <div className='w-100 sm-margin-top'>{record.description || '-'}</div>

          {renderViewCopyLabel('urlDev', translate('externalApisManagement.edit.urlDev'), record.urlDev)}
          <div className='w-100 sm-margin-top external-apis-management-view-value'>{record.urlDev || '-'}</div>

          {renderViewCopyLabel('urlPrd', translate('externalApisManagement.edit.urlPrd'), record.urlPrd)}
          <div className='w-100 sm-margin-top external-apis-management-view-value'>{record.urlPrd || '-'}</div>

          {renderViewCopyLabel('apiKeyDev', translate('externalApisManagement.edit.apiKeyDev'), record.apiKeyDev)}
          <div className='w-100 sm-margin-top'>{record.apiKeyDev || '-'}</div>

          {renderViewCopyLabel('apiKeyPrd', translate('externalApisManagement.edit.apiKeyPrd'), record.apiKeyPrd)}
          <div className='w-100 sm-margin-top'>{record.apiKeyPrd || '-'}</div>

          <div className='container-parameter' style={{ marginTop: 16 }}>
            <div className='parameter-item'>
              <div className='w-100 margin-top color-default'>
                {translate('externalApisManagement.table.devUpdatedAt')}
              </div>
              <div className='w-100 sm-margin-top'>
                {isVaultRecord && record.devUpdatedAt
                  ? moment(record.devUpdatedAt).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </div>
            </div>
            <div>
              <div className='w-100 margin-top color-default'>
                {translate('externalApisManagement.table.prdUpdatedAt')}
              </div>
              <div className='w-100 sm-margin-top'>
                {isVaultRecord && record.prdUpdatedAt
                  ? moment(record.prdUpdatedAt).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      title={translate('externalApisManagement.edit.title')}
      confirmButtonText={translate('save_button_label')}
      cancelButtonText={translate('cancel_button')}
      isOpen
      size='md'
      className='external-apis-management-modal'
      onCloseModal={onClose}
      onConfirm={handleSave}
      isConfirmButtonDisabled={!hasChanges() || !hasRequiredFieldsFilled() || isUpdating}
    >
      <div className='external-apis-management-edit-modal'>
        <div className='external-apis-management-info-grid'>
          <div>
            <div className='external-apis-management-info-label'>
              {translate('externalApisManagement.table.name')}
            </div>
            <div>{record.name}</div>
          </div>

          <div>
            <div className='external-apis-management-info-label'>
              {translate('externalApisManagement.table.syncVault')}
            </div>
            <div>
              {record.usesVault
                ? translate('externalApisManagement.target.vault')
                : translate('externalApisManagement.target.custom')}
            </div>
          </div>
        </div>

        <div className='external-apis-management-description-wrapper'>
          <TextArea
            controlled
            required
            label={translate('externalApisManagement.table.description')}
            name='description'
            value={formData.description}
            onChange={handleChange('description')}
          />
        </div>

        <div className='external-apis-management-fields-grid'>
          {renderCopyableTextArea({
            field: 'urlDev',
            label: translate('externalApisManagement.edit.urlDev'),
            value: formData.urlDev,
            onChange: handleChange('urlDev'),
            ariaLabel: translate('externalApisManagement.table.copyValue'),
          })}

          {renderCopyableTextArea({
            field: 'urlPrd',
            label: translate('externalApisManagement.edit.urlPrd'),
            value: formData.urlPrd,
            onChange: handleChange('urlPrd'),
            ariaLabel: translate('externalApisManagement.table.copyValue'),
          })}
        </div>

        <div className='external-apis-management-fields-grid' style={{ marginTop: 16 }}>
          {renderCopyableTextField({
            field: 'apiKeyDev',
            label: translate('externalApisManagement.edit.apiKeyDev'),
            value: formData.apiKeyDev,
            onChange: handleChange('apiKeyDev'),
            ariaLabel: translate('externalApisManagement.edit.copyApiKeyDev'),
            disabled: isVaultRecord,
          })}

          {renderCopyableTextField({
            field: 'apiKeyPrd',
            label: translate('externalApisManagement.edit.apiKeyPrd'),
            value: formData.apiKeyPrd,
            onChange: handleChange('apiKeyPrd'),
            ariaLabel: translate('externalApisManagement.edit.copyApiKeyPrd'),
            disabled: isVaultRecord,
          })}
        </div>
      </div>
    </Modal>
  )
}

ExternalApiEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  record: PropTypes.object,
  translate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  isUpdating: PropTypes.bool,
  readOnly: PropTypes.bool,
}

export default ExternalApiEditModal
